import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { clientLLMService } from '../services/clientLLMService';
import type { ClientLLMConfiguration, LLMModel } from '../services/clientLLMService';
import { derivativesService, type FunctionPromptMapping } from '../services/derivativesService';
import { useAuth } from '../context/AuthContextType';
import PageHeader from '@/components/layout/PageHeader';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

const SettingsGeneral: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Function prompts state
  const [functionPrompts, setFunctionPrompts] = useState<Record<string, FunctionPromptMapping>>({});
  const [showCreateFunctionForm, setShowCreateFunctionForm] = useState(false);
  const [newFunction, setNewFunction] = useState({
    function_name: '',
    label_fr: '',
    label_en: '',
    prompt_content: '',
    description: '',
    requires_markdown: true
  });

  // Client-level LLM configuration state
  const [clientLLMModels, setClientLLMModels] = useState<LLMModel[]>([]);
  const [clientLLMConfig, setClientLLMConfig] = useState<ClientLLMConfiguration | null>(null);
  const [isClientConfigured, setIsClientConfigured] = useState(false);
  const [clientLLMLoading, setClientLLMLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  // Default (system-wide) LLM configuration state
  const [defaultLLMConfig, setDefaultLLMConfig] = useState<ClientLLMConfiguration | null>(null);
  const [isDefaultConfigured, setIsDefaultConfigured] = useState(false);
  const [defaultLLMLoading, setDefaultLLMLoading] = useState(false);
  const [defaultConfigError, setDefaultConfigError] = useState<string | null>(null);

  // Function-specific LLM configuration state
  const [functionLLMConfig, setFunctionLLMConfig] = useState<Record<string, { model_id: string; model_host: string }>>({});
  const [functionLLMLoading, setFunctionLLMLoading] = useState(false);

  const canConfigureLLMs = !!user && ['SysAdmin', 'DevOps', 'ClientAdmin'].includes(user.role || '');
  const canModifyClientLLMs = !!user && user.role === 'ClientAdmin';
  const canConfigureDefaultLLMs = !!user && ['SysAdmin', 'DevOps'].includes(user.role || '');

  useEffect(() => {
    loadFunctionPromptsMapping();
  }, []);

  useEffect(() => {
    if (user && canConfigureLLMs) {
      loadClientLLMConfig();
    }
  }, [user, canConfigureLLMs]);

  useEffect(() => {
    if (user && canConfigureDefaultLLMs) {
      loadDefaultLLMConfig();
      loadFunctionLLMConfig();
    }
  }, [user, canConfigureDefaultLLMs]);

  const loadFunctionPromptsMapping = async () => {
    try {
      setLoading(true);
      const mappings = await derivativesService.getFunctionPromptsMapping();
      setFunctionPrompts(mappings);
    } catch (error: unknown) {
      console.error('Error loading function prompts mapping:', error);
      const e = error as { response?: { status?: number; data?: { detail?: string } }; message?: string }; if (e.response?.status === 403) {
        setFunctionPrompts({});
      } else {
        showStatus(t('pages.settingsGeneral.errorLoadFunctions'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadClientLLMConfig = async () => {
    if (!user?.client_id) return;

    try {
      setClientLLMLoading(true);
      setConfigError(null);
      const [models, config] = await Promise.all([
        clientLLMService.getAvailableModels(),
        clientLLMService.getClientLLMConfig(user.client_id)
      ]);

      setClientLLMModels(models);
      setClientLLMConfig({
        fast_LLM: {
          model_id: config.llm_configuration.fast_LLM.model_id,
          model_host: config.llm_configuration.fast_LLM.model_host
        },
        slow_LLM: {
          model_id: config.llm_configuration.slow_LLM.model_id,
          model_host: config.llm_configuration.slow_LLM.model_host
        },
        long_context_LLM: {
          model_id: config.llm_configuration.long_context_LLM.model_id,
          model_host: config.llm_configuration.long_context_LLM.model_host
        }
      });
      setIsClientConfigured(config.is_configured);
    } catch (error: unknown) {
      console.error('Error loading client LLM config:', error);
      const e = error as { response?: { data?: { detail?: string } }; message?: string }; const errorMsg = e.response?.data?.detail || e.message || 'Failed to load configuration';
      setConfigError(errorMsg);
      showStatus(`Error: ${errorMsg}`, 'error');
    } finally {
      setClientLLMLoading(false);
    }
  };

  const loadDefaultLLMConfig = async () => {
    try {
      setDefaultLLMLoading(true);
      setDefaultConfigError(null);
      const [models, config] = await Promise.all([
        clientLLMService.getAvailableModels(),
        clientLLMService.getDefaultLLMConfig()
      ]);

      if (clientLLMModels.length === 0) {
        setClientLLMModels(models);
      }

      const dl = config.default_llm_configuration;
      if (!dl?.fast_LLM || !dl?.slow_LLM || !dl?.long_context_LLM) {
        throw new Error('Default LLM configuration is incomplete or uses old format. Please refresh or contact your administrator.');
      }
      setDefaultLLMConfig({
        fast_LLM: { model_id: dl.fast_LLM.model_id, model_host: dl.fast_LLM.model_host },
        slow_LLM: { model_id: dl.slow_LLM.model_id, model_host: dl.slow_LLM.model_host },
        long_context_LLM: { model_id: dl.long_context_LLM.model_id, model_host: dl.long_context_LLM.model_host }
      });
      setIsDefaultConfigured(config.is_configured);
    } catch (error: unknown) {
      console.error('Error loading default LLM config:', error);
      const e = error as { response?: { data?: { detail?: string } }; message?: string }; const errorMsg = e.response?.data?.detail || e.message || t('pages.settingsGeneral.errorLoadDefault');
      setDefaultConfigError(errorMsg);
      showStatus(`Error: ${errorMsg}`, 'error');
    } finally {
      setDefaultLLMLoading(false);
    }
  };

  const retryLoadDefaultConfig = () => {
    setDefaultConfigError(null);
    loadDefaultLLMConfig();
  };

  const loadFunctionLLMConfig = async () => {
    try {
      setFunctionLLMLoading(true);
      const response = await clientLLMService.getFunctionLLMConfig();
      setFunctionLLMConfig(response.function_llm_config || {});
    } catch (error: unknown) {
      console.error('Error loading function LLM config:', error);
    } finally {
      setFunctionLLMLoading(false);
    }
  };

  const saveFunctionLLMConfig = async () => {
    try {
      setFunctionLLMLoading(true);
      await clientLLMService.updateFunctionLLMConfig(functionLLMConfig);
      showStatus(t('pages.settingsGeneral.successSaveFunctionLLM'), 'success');
    } catch (error: unknown) {
      console.error('Error saving function LLM config:', error);
      const e = error as { response?: { data?: { detail?: string } }; message?: string }; const errorMsg = e.response?.data?.detail || e.message || 'Failed to save';
      showStatus(`Error: ${errorMsg}`, 'error');
    } finally {
      setFunctionLLMLoading(false);
    }
  };

  const updateFunctionLLM = (functionName: string, modelId: string) => {
    if (!modelId) {
      const updated = { ...functionLLMConfig };
      delete updated[functionName];
      setFunctionLLMConfig(updated);
      return;
    }
    const selectedModel = clientLLMModels.find(m => m.model_id === modelId);
    if (!selectedModel) return;
    setFunctionLLMConfig({
      ...functionLLMConfig,
      [functionName]: { model_id: selectedModel.model_id, model_host: selectedModel.model_host }
    });
  };

  const retryLoadConfig = () => {
    setConfigError(null);
    loadClientLLMConfig();
  };

  const handleCreateFunction = async () => {
    if (!newFunction.function_name.trim() || !newFunction.prompt_content.trim()) {
      showStatus(t('pages.settingsGeneral.errorFunctionRequired'), 'error');
      return;
    }

    try {
      setLoading(true);
      await derivativesService.saveFunctionPrompt(newFunction);
      showStatus(t('pages.settingsGeneral.successCreateFunction', { name: newFunction.function_name }), 'success');

      await loadFunctionPromptsMapping();

      setNewFunction({
        function_name: '',
        label_fr: '',
        label_en: '',
        prompt_content: '',
        description: '',
        requires_markdown: true
      });
      setShowCreateFunctionForm(false);
    } catch (error: unknown) {
      console.error('Error creating function:', error);
      const e = error as { response?: { data?: { detail?: string } } }; const errorMsg = e.response?.data?.detail || t('pages.settingsGeneral.errorCreateFunction');
      showStatus(`Error: ${errorMsg}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveClientLLMConfig = async () => {
    if (!user?.client_id || !clientLLMConfig) return;

    try {
      setClientLLMLoading(true);
      await clientLLMService.updateClientLLMConfig(user.client_id, clientLLMConfig);
      showStatus(t('pages.settingsGeneral.successSaveClient'), 'success');
      setIsClientConfigured(true);
    } catch (error: unknown) {
      console.error('Error saving client LLM config:', error);
      const e = error as { response?: { data?: { detail?: string } }; message?: string }; const errorMsg = e.response?.data?.detail || e.message || t('pages.settingsGeneral.errorSaveClient');
      showStatus(`Error: ${errorMsg}`, 'error');
    } finally {
      setClientLLMLoading(false);
    }
  };

  const saveDefaultLLMConfig = async () => {
    if (!defaultLLMConfig) return;

    try {
      setDefaultLLMLoading(true);
      await clientLLMService.updateDefaultLLMConfig(defaultLLMConfig);
      showStatus(t('pages.settingsGeneral.successSaveDefault'), 'success');
      setIsDefaultConfigured(true);
    } catch (error: unknown) {
      console.error('Error saving default LLM config:', error);
      const e = error as { response?: { data?: { detail?: string } }; message?: string }; const errorMsg = e.response?.data?.detail || e.message || t('pages.settingsGeneral.errorSaveDefault');
      showStatus(`Error: ${errorMsg}`, 'error');
    } finally {
      setDefaultLLMLoading(false);
    }
  };

  const updateClientLLMCategory = (category: keyof ClientLLMConfiguration, modelId: string) => {
    if (!clientLLMConfig) return;

    const selectedModel = clientLLMModels.find(m => m.model_id === modelId);
    if (!selectedModel) return;

    setClientLLMConfig({
      ...clientLLMConfig,
      [category]: {
        model_id: selectedModel.model_id,
        model_host: selectedModel.model_host
      }
    });
  };

  const updateDefaultLLMCategory = (category: keyof ClientLLMConfiguration, modelId: string) => {
    if (!defaultLLMConfig) return;

    const selectedModel = clientLLMModels.find(m => m.model_id === modelId);
    if (!selectedModel) return;

    setDefaultLLMConfig({
      ...defaultLLMConfig,
      [category]: {
        model_id: selectedModel.model_id,
        model_host: selectedModel.model_host
      }
    });
  };

  const showStatus = (text: string, type: 'success' | 'error' | 'info') => {
    setStatusMessage({ text, type });
    setTimeout(() => setStatusMessage(null), 3000);
  };

  const getCategoryUsageDescription = (category: string): string[] => {
    const usageMap: Record<string, string[]> = {
      'fast_LLM': [
        'Patent search agent (KSA)',
        'Search query refinement',
        'Relevance assessment and ranking',
        'Voice chatbot responses',
        'Quick document summaries'
      ],
      'slow_LLM': [
        'Patent claims generation (Projects/Sub-projects)',
        'Invention disclosures and detailed analysis',
        'Complex prior art comparisons',
        'IP-sensitive document summaries'
      ],
      'long_context_LLM': [
        'KB chatbot (large document sets)',
        'Operations requiring large context windows',
        'Multi-document synthesis'
      ]
    };
    return usageMap[category] || [];
  };

  const formatContextSize = (contextSize: number): string => {
    if (contextSize >= 1000000) return `${(contextSize / 1000000).toFixed(1)}M`;
    return `${(contextSize / 1000).toFixed(0)}K`;
  };

  const renderLLMCategory = (
    category: keyof ClientLLMConfiguration,
    label: string,
    config: ClientLLMConfiguration,
    onChange: (cat: keyof ClientLLMConfiguration, modelId: string) => void,
    disabled: boolean
  ) => {
    const usage = getCategoryUsageDescription(category);
    return (
      <div key={category} className="flex items-center gap-3 py-1.5">
        <span className="w-36 text-sm text-slate-600 flex-shrink-0">{label}</span>
        <select
          value={config[category].model_id}
          onChange={(e) => onChange(category, e.target.value)}
          disabled={disabled}
          className="flex-1 text-sm border border-slate-200 rounded px-2 py-1 bg-white disabled:bg-slate-50 disabled:text-slate-400 focus:outline-none focus:border-primary-400"
        >
          {clientLLMModels.map(model => (
            <option key={model.model_id} value={model.model_id}>
              {model.model_name} ({model.model_host}) · {formatContextSize(model.context_size)}
            </option>
          ))}
        </select>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-slate-300 hover:text-slate-500 cursor-help flex-shrink-0"><Info size={14} /></span>
          </TooltipTrigger>
          <TooltipContent>
            <ul className="text-xs space-y-0.5">
              {usage.map((u, i) => <li key={i}>· {u}</li>)}
            </ul>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  };

  const Divider = () => <hr className="border-slate-100 my-3" />;

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title={t('pages.settingsGeneral.title')} />

      {statusMessage && (
        <div className={`mx-6 mt-3 px-3 py-2 rounded text-sm ${
          statusMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' :
          statusMessage.type === 'error' ? 'bg-red-50 text-red-700 border border-red-100' :
          'bg-blue-50 text-blue-700 border border-blue-100'
        }`}>
          {statusMessage.text}
        </div>
      )}

      <div className="px-6 pb-6">

        {/* Default LLM Configuration */}
        {canConfigureDefaultLLMs && (
          <>
            <section className="pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm font-semibold text-slate-700">{t('pages.settingsGeneral.defaultLLMConfig')}</h3>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-slate-300 hover:text-slate-500 cursor-help"><Info size={13} /></span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">{t('pages.settingsGeneral.defaultLLMNote')}</p>
                    </TooltipContent>
                  </Tooltip>
                  {isDefaultConfigured && <span className="text-xs text-green-600 font-medium">● Active</span>}
                </div>
                <Button size="sm" variant="outline" onClick={saveDefaultLLMConfig} disabled={defaultLLMLoading || !defaultLLMConfig}>
                  {defaultLLMLoading ? t('common.saving') : t('pages.settingsGeneral.saveDefault')}
                </Button>
              </div>
              {defaultConfigError ? (
                <div className="flex items-center gap-2 text-sm text-red-600 py-1">
                  <span>{defaultConfigError}</span>
                  <Button size="sm" variant="ghost" onClick={retryLoadDefaultConfig}>{t('pages.settingsGeneral.retryLoadDefault')}</Button>
                </div>
              ) : defaultLLMLoading ? (
                <p className="text-sm text-slate-400 py-1">{t('pages.settingsGeneral.loadingDefault')}</p>
              ) : defaultLLMConfig ? (
                <div>
                  {renderLLMCategory('fast_LLM', t('pages.settingsGeneral.llmCategories.fast'), defaultLLMConfig, updateDefaultLLMCategory, false)}
                  {renderLLMCategory('slow_LLM', t('pages.settingsGeneral.llmCategories.slow'), defaultLLMConfig, updateDefaultLLMCategory, false)}
                  {renderLLMCategory('long_context_LLM', t('pages.settingsGeneral.llmCategories.longContext'), defaultLLMConfig, updateDefaultLLMCategory, false)}
                </div>
              ) : (
                <p className="text-sm text-slate-400 py-1">{t('pages.settingsGeneral.noDefaultConfig')}</p>
              )}
            </section>
            <Divider />
          </>
        )}

        {/* Client LLM Configuration */}
        {canConfigureLLMs && (
          <>
            <section className="pt-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm font-semibold text-slate-700">{t('pages.settingsGeneral.llmModelSelection')}</h3>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-slate-300 hover:text-slate-500 cursor-help"><Info size={13} /></span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">{t('pages.settingsGeneral.llmOrgWideNote')}</p>
                      {!canModifyClientLLMs && <p className="text-xs mt-1 text-slate-300">{t('pages.settingsGeneral.viewOnlyMessage')}</p>}
                    </TooltipContent>
                  </Tooltip>
                  {isClientConfigured && <span className="text-xs text-green-600 font-medium">● Active</span>}
                </div>
                {canModifyClientLLMs ? (
                  <Button size="sm" variant="outline" onClick={saveClientLLMConfig} disabled={clientLLMLoading || !clientLLMConfig}>
                    {clientLLMLoading ? t('common.saving') : t('pages.settingsGeneral.saveConfig')}
                  </Button>
                ) : (
                  <span className="text-xs text-slate-400">{t('pages.settingsGeneral.viewOnly')}</span>
                )}
              </div>
              {configError ? (
                <div className="flex items-center gap-2 text-sm text-red-600 py-1">
                  <span>{configError}</span>
                  <Button size="sm" variant="ghost" onClick={retryLoadConfig}>{t('pages.settingsGeneral.retryLoadConfig')}</Button>
                </div>
              ) : clientLLMLoading ? (
                <p className="text-sm text-slate-400 py-1">{t('pages.settingsGeneral.loadingClient')}</p>
              ) : clientLLMConfig ? (
                <div>
                  {renderLLMCategory('fast_LLM', t('pages.settingsGeneral.llmCategories.fast'), clientLLMConfig, updateClientLLMCategory, !canModifyClientLLMs)}
                  {renderLLMCategory('slow_LLM', t('pages.settingsGeneral.llmCategories.slow'), clientLLMConfig, updateClientLLMCategory, !canModifyClientLLMs)}
                  {renderLLMCategory('long_context_LLM', t('pages.settingsGeneral.llmCategories.longContext'), clientLLMConfig, updateClientLLMCategory, !canModifyClientLLMs)}
                </div>
              ) : (
                <p className="text-sm text-slate-400 py-1">{t('pages.settingsGeneral.noConfig')}</p>
              )}
            </section>
            <Divider />
          </>
        )}

        {/* Function-specific LLM Configuration — SysAdmin/DevOps only */}
        {canConfigureDefaultLLMs && (
          <>
            <section className="pt-1">
              <div className="flex items-center justify-between py-1.5">
                <h3 className="text-sm font-semibold text-slate-700">{t('pages.settingsGeneral.functionLLMTitle')}</h3>
                <Button size="sm" onClick={saveFunctionLLMConfig} disabled={functionLLMLoading}>
                  {functionLLMLoading ? t('common.saving') : t('common.save')}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-2">{t('pages.settingsGeneral.functionLLMDescription')}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <label className="text-sm text-slate-600 w-48">{t('pages.settingsGeneral.functionLLM_cpc')}</label>
                  <select
                    className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm"
                    value={functionLLMConfig.cpc_suggestion?.model_id || ''}
                    onChange={e => updateFunctionLLM('cpc_suggestion', e.target.value)}
                  >
                    <option value="">{t('pages.settingsGeneral.functionLLMDefault')}</option>
                    {clientLLMModels.map(model => (
                      <option key={model.model_id} value={model.model_id}>
                        {model.model_id} ({model.model_host})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>
            <Divider />
          </>
        )}

        {/* Database + App Info — single info row each */}
        <section className="pt-1 flex flex-wrap items-center gap-x-4 gap-y-1 py-1.5">
          <h3 className="text-sm font-semibold text-slate-700 flex-shrink-0">{t('pages.settingsGeneral.databaseSettings')}</h3>
          <span className="text-sm text-slate-500">{t('pages.settingsGeneral.mongodbStatus')}: <span className="text-green-600 font-medium">● {t('pages.settingsGeneral.connected')}</span></span>
          <span className="text-slate-200">│</span>
          <span className="text-sm text-slate-500">{t('pages.settingsGeneral.chromadbStatus')}: <span className="text-amber-500">○ {t('pages.settingsGeneral.notConfigured')}</span></span>
        </section>

        <section className="flex flex-wrap items-center gap-x-4 gap-y-1 py-1.5">
          <h3 className="text-sm font-semibold text-slate-700 flex-shrink-0">{t('pages.settingsGeneral.appInfo')}</h3>
          <span className="text-sm text-slate-500">{t('pages.settingsGeneral.version')}: <span className="text-slate-700">1.0.0</span></span>
          <span className="text-slate-200">│</span>
          <span className="text-sm text-slate-500">{t('pages.settingsGeneral.environment')}: <span className="text-slate-700">Development</span></span>
          <span className="text-slate-200">│</span>
          <span className="text-sm text-slate-500">{t('pages.settingsGeneral.backend')}: <span className="text-slate-700">FastAPI + MongoDB</span></span>
        </section>

        <Divider />

        {/* Document Functions Configuration */}
        <section className="pt-1">
          <div className="flex items-center justify-between py-1.5">
            <h3 className="text-sm font-semibold text-slate-700">{t('pages.settingsGeneral.docFunctionsConfig')}</h3>
            <Button size="sm" variant="outline" onClick={() => setShowCreateFunctionForm(!showCreateFunctionForm)}>
              {showCreateFunctionForm ? t('common.cancel') : t('pages.settingsGeneral.createNewFunction')}
            </Button>
          </div>

          {Object.keys(functionPrompts).length > 0 ? (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-slate-100">
                  {[t('pages.settingsGeneral.functionName'), t('pages.settingsGeneral.labelFr'), t('pages.settingsGeneral.labelEn'), t('common.description'), t('pages.settingsGeneral.promptFile'), t('pages.settingsGeneral.requiresMarkdown')].map(h => (
                    <th key={h} className="text-left py-1.5 pr-3 text-xs font-medium text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(functionPrompts).map(([name, mapping]) => (
                  <tr key={name} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-1.5 pr-3 text-slate-700">{name}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{mapping.label_fr || name}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{mapping.label_en || name}</td>
                    <td className="py-1.5 pr-3 text-slate-500 text-xs">{mapping.description}</td>
                    <td className="py-1.5 pr-3"><code className="text-xs bg-slate-100 rounded px-1">{mapping.prompt_file}</code></td>
                    <td className="py-1.5 text-slate-500 text-xs">{mapping.requires_markdown ? t('common.yes') : t('common.no')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-slate-400 py-1">{t('pages.settingsGeneral.noFunctions')}</p>
          )}

          {showCreateFunctionForm && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
              <h4 className="text-sm font-medium text-slate-700">{t('pages.settingsGeneral.createNewFunction')}</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">{t('pages.settingsGeneral.functionName')}</label>
                  <input type="text" value={newFunction.function_name}
                    onChange={(e) => setNewFunction({ ...newFunction, function_name: e.target.value })}
                    placeholder={t('pages.settingsGeneral.functionNamePlaceholder')}
                    className="w-full text-sm border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">{t('pages.settingsGeneral.labelFr')}</label>
                  <input type="text" value={newFunction.label_fr}
                    onChange={(e) => setNewFunction({ ...newFunction, label_fr: e.target.value })}
                    placeholder={t('pages.settingsGeneral.labelFrPlaceholder')}
                    className="w-full text-sm border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">{t('pages.settingsGeneral.labelEn')}</label>
                  <input type="text" value={newFunction.label_en}
                    onChange={(e) => setNewFunction({ ...newFunction, label_en: e.target.value })}
                    placeholder={t('pages.settingsGeneral.labelEnPlaceholder')}
                    className="w-full text-sm border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('common.description')}</label>
                <input type="text" value={newFunction.description}
                  onChange={(e) => setNewFunction({ ...newFunction, description: e.target.value })}
                  placeholder={t('pages.settingsGeneral.descriptionPlaceholder')}
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('pages.settingsGeneral.promptContent')}</label>
                <textarea value={newFunction.prompt_content}
                  onChange={(e) => setNewFunction({ ...newFunction, prompt_content: e.target.value })}
                  rows={10} placeholder={t('pages.settingsGeneral.promptContentPlaceholder')}
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1 font-mono focus:outline-none focus:border-primary-400"
                />
                <p className="text-xs text-slate-400 mt-0.5">
                  {t('pages.settingsGeneral.promptSavedAs')}: <code className="bg-slate-100 rounded px-1">{newFunction.function_name.toLowerCase().replace(/ /g, '_')}_prompt.txt</code>
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={newFunction.requires_markdown}
                  onChange={(e) => setNewFunction({ ...newFunction, requires_markdown: e.target.checked })}
                />
                {t('pages.settingsGeneral.requiresMarkdownLabel')}
              </label>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleCreateFunction} disabled={loading}>
                  {loading ? t('common.saving') : t('pages.settingsGeneral.createFunction')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowCreateFunctionForm(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SettingsGeneral;
